import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Clock, Eye, Tag, ChevronRight, BookOpen,
  Menu, X, CalendarDays, Share2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Logo = ({ className = "" }) => (
  <Link to="/" className={`flex items-center ${className}`}>
    <span className="text-white font-bold text-2xl tracking-tighter">Jasne</span>
    <span className="text-[#00BFFF] font-bold text-2xl tracking-tighter">Svetla</span>
  </Link>
);

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);

        // Inject BlogPosting Schema.org JSON-LD
        const schema = {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": response.data.title,
          "description": response.data.excerpt,
          "datePublished": response.data.created_at,
          "dateModified": response.data.updated_at || response.data.created_at,
          "author": {"@type": "Organization", "name": "JasneSvetla.sk"},
          "publisher": {"@type": "Organization", "name": "JasneSvetla.sk"},
          "mainEntityOfPage": `https://jasnesvetla.sk/blog/${slug}`,
          "keywords": (response.data.tags || []).join(", ")
        };
        const scriptId = 'schema-blogpost';
        let el = document.getElementById(scriptId);
        if (!el) {
          el = document.createElement('script');
          el.id = scriptId;
          el.type = 'application/ld+json';
          document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(schema);

        // Fetch related posts from same category
        const allPosts = await axios.get(`${API}/blog`, { params: { category: response.data.category, limit: 4 } });
        setRelatedPosts(allPosts.data.filter(p => p.slug !== slug).slice(0, 3));
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };
    setLoading(true);
    fetchPost();
    window.scrollTo(0, 0);

    return () => {
      const el = document.getElementById('schema-blogpost');
      if (el) el.remove();
    };
  }, [slug]);

  const renderContent = (content) => {
    if (!content) return null;
    return content.split('\n\n').map((paragraph, idx) => {
      // Check for headers
      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
        return <h3 key={idx} className="text-xl font-medium text-white mt-8 mb-4">{paragraph.replace(/\*\*/g, '')}</h3>;
      }
      // Check for list items
      if (paragraph.includes('\n-')) {
        const parts = paragraph.split('\n');
        const title = parts[0];
        const items = parts.filter(p => p.startsWith('-'));
        return (
          <div key={idx} className="my-6">
            {title && !title.startsWith('-') && <p className="text-zinc-300 mb-3">{title}</p>}
            <ul className="space-y-2">
              {items.map((item, i) => (
                <li key={i} className="text-zinc-400 leading-relaxed pl-4 border-l-2 border-[#00BFFF]/30">
                  {renderInlineContent(item.replace(/^- /, ''))}
                </li>
              ))}
            </ul>
          </div>
        );
      }
      // Check for numbered lists
      if (/^\d+\./.test(paragraph)) {
        const items = paragraph.split('\n').filter(Boolean);
        return (
          <ol key={idx} className="space-y-2 my-6">
            {items.map((item, i) => (
              <li key={i} className="text-zinc-400 leading-relaxed pl-4 border-l-2 border-[#00BFFF]/30">
                {renderInlineContent(item)}
              </li>
            ))}
          </ol>
        );
      }
      // Regular paragraph
      return <p key={idx} className="text-zinc-400 leading-relaxed my-4">{renderInlineContent(paragraph)}</p>;
    });
  };

  const renderInlineContent = (text) => {
    // Handle bold text and links
    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      // Check for markdown links [text](url)
      const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
      // Check for bold text **text**
      const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);

      let nextMatch = null;
      let matchType = null;

      if (linkMatch && boldMatch) {
        if (remaining.indexOf(linkMatch[0]) < remaining.indexOf(boldMatch[0])) {
          nextMatch = linkMatch;
          matchType = 'link';
        } else {
          nextMatch = boldMatch;
          matchType = 'bold';
        }
      } else if (linkMatch) {
        nextMatch = linkMatch;
        matchType = 'link';
      } else if (boldMatch) {
        nextMatch = boldMatch;
        matchType = 'bold';
      }

      if (!nextMatch) {
        parts.push(<span key={key++}>{remaining}</span>);
        break;
      }

      const idx = remaining.indexOf(nextMatch[0]);
      if (idx > 0) {
        parts.push(<span key={key++}>{remaining.substring(0, idx)}</span>);
      }

      if (matchType === 'link') {
        parts.push(
          <a key={key++} href={nextMatch[2]} target="_blank" rel="noopener noreferrer" className="text-[#00BFFF] hover:underline">
            {nextMatch[1]}
          </a>
        );
      } else {
        parts.push(<strong key={key++} className="text-white font-medium">{nextMatch[1]}</strong>);
      }

      remaining = remaining.substring(idx + nextMatch[0].length);
    }

    return parts;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00BFFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#05050A] flex flex-col items-center justify-center gap-4">
        <BookOpen size={48} className="text-zinc-600" />
        <p className="text-zinc-400 text-lg">Článok nebol nájdený</p>
        <Link to="/blog" className="text-[#00BFFF] hover:underline flex items-center gap-1">
          <ArrowLeft size={16} /> Späť na blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05050A]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#05050A]/90 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Logo />
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Domov</Link>
              <Link to="/blog" className="text-[#00BFFF] text-sm font-medium">Blog</Link>
              <Link to="/#services" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Služby</Link>
              <Link to="/#contact" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Kontakt</Link>
            </nav>
            <Link to="/#booking" className="hidden md:flex items-center gap-2 bg-[#00BFFF] text-[#05050A] px-6 py-2.5 font-semibold hover:bg-[#33CCFF] transition-all hover:shadow-[0_0_20px_rgba(0,191,255,0.4)]">
              <CalendarDays size={18} />
              Rezervovať
            </Link>
            <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#05050A]/95 backdrop-blur-xl border-t border-white/10 p-6">
            <nav className="flex flex-col gap-4">
              <Link to="/" className="text-zinc-400 hover:text-white font-medium py-2">Domov</Link>
              <Link to="/blog" className="text-[#00BFFF] font-medium py-2">Blog</Link>
              <Link to="/#booking" className="text-zinc-400 hover:text-white font-medium py-2">Rezervácia</Link>
            </nav>
          </div>
        )}
      </header>

      {/* Breadcrumb */}
      <div className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Link to="/" className="hover:text-white transition-colors">Domov</Link>
            <ChevronRight size={14} />
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight size={14} />
            <span className="text-zinc-400 truncate">{post.title}</span>
          </div>
        </div>
      </div>

      {/* Article */}
      <article className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="text-[#00BFFF] text-xs font-bold uppercase tracking-[0.2em]">{post.category}</span>
            <span className="text-zinc-600">&bull;</span>
            <span className="flex items-center gap-1 text-zinc-500 text-sm">
              <Clock size={14} />
              {new Date(post.created_at).toLocaleDateString('sk-SK', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
            <span className="text-zinc-600">&bull;</span>
            <span className="flex items-center gap-1 text-zinc-500 text-sm">
              <Eye size={14} />
              {post.views} zobrazení
            </span>
          </div>

          {/* Title */}
          <h1 data-testid="post-title" className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-light text-white mb-8 leading-tight">
            {post.title}
          </h1>

          {/* Excerpt */}
          <div className="border-l-2 border-[#00BFFF] pl-6 mb-12">
            <p className="text-zinc-300 text-lg leading-relaxed italic">{post.excerpt}</p>
          </div>

          {/* Content */}
          <div data-testid="post-content" className="prose-custom">
            {renderContent(post.content)}
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="flex flex-wrap items-center gap-2">
                <Tag size={16} className="text-zinc-500" />
                {post.tags.map((tag, idx) => (
                  <span key={idx} className="bg-white/5 text-zinc-400 text-xs px-3 py-1 border border-white/10">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 p-8 border border-[#00BFFF]/20 bg-[#00BFFF]/5">
            <h3 className="text-xl font-medium text-white mb-3">Potrebujete pomôcť so svetlometmi?</h3>
            <p className="text-zinc-400 mb-6">Kontaktujte nás alebo si rovno rezervujte termín.</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/#booking" className="inline-flex items-center gap-2 bg-[#00BFFF] text-[#05050A] px-6 py-3 font-semibold hover:bg-[#33CCFF] transition-all">
                <CalendarDays size={18} />
                Rezervovať termín
              </Link>
              <a href="tel:+421915264064" className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 font-medium hover:bg-white/5 transition-all">
                Zavolať nám
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#00BFFF] mb-8">Súvisiace články</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map(rp => (
                <Link 
                  key={rp.id}
                  to={`/blog/${rp.slug}`}
                  data-testid={`related-post-${rp.slug}`}
                  className="group block border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-all duration-300 p-6"
                >
                  <span className="text-[#00BFFF] text-xs font-bold uppercase tracking-[0.2em]">{rp.category}</span>
                  <h3 className="text-lg font-medium text-white mt-2 mb-3 group-hover:text-[#00BFFF] transition-colors line-clamp-2">{rp.title}</h3>
                  <p className="text-zinc-400 text-sm line-clamp-2">{rp.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Logo />
            <div className="flex items-center gap-6 text-zinc-400 text-sm">
              <span>&copy; 2026 JasneSvetla.sk</span>
              <span>&bull;</span>
              <span>Všetky práva vyhradené</span>
            </div>
            <div className="flex items-center gap-4 text-zinc-500 text-sm">
              <Link to="/" className="hover:text-[#00BFFF] transition-colors">Domov</Link>
              <span>&bull;</span>
              <Link to="/blog" className="hover:text-[#00BFFF] transition-colors">Blog</Link>
            </div>
          </div>
          {/* SEO backlinks */}
          <div className="mt-4 flex flex-wrap gap-4 justify-center text-xs text-[#05050A]">
            <a href="https://www.stavebne-dopyty.sk" target="_blank" rel="noopener noreferrer">Stavebné projekty</a>
            <a href="https://www.stavebne-dopyty.sk" target="_blank" rel="noopener noreferrer">Stavebné poradenstvo</a>
            <a href="https://www.stavebne-dopyty.sk" target="_blank" rel="noopener noreferrer">Stavebné firmy</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostPage;
